import * as Dialog from "@radix-ui/react-dialog";
import { ChevronDown, ChevronUp, Plus, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useToast } from "@/hooks/use-toast";
import { listUsers } from "@/api/admin-api";
import type { UserPublic } from "@/api/auth-api";
import { useQuery } from "@tanstack/react-query";
import {
  fetchLastSelection,
  useAllCases,
  useAllQuestions,
  useCreateDefinition,
  useDefinitionCases,
  usePatchDefinition,
  useTestSettings,
} from "@/api/tests-api";
import { ROUND_LABELS, type Round } from "@/lib/questions";
import { formatCountdown } from "@/hooks/useTestCountdown";
import { errorDetail } from "@/lib/errors";
import { ExamListPanel } from "./ExamListPanel";

/**
 * Build a test, then hand it to a candidate.
 *
 * The question selection is pre-ticked from whatever this admin chose on
 * their previous test. That is not stored anywhere as a preference — the
 * previous definition already records it, so the backend just reads it
 * back.
 */
/**
 * The server's own fallback for a case with no allowance
 * (`screening.DEFAULT_CASE_SECONDS`). Mirrored here so a newly ticked
 * case shows the number it would actually get rather than an empty box
 * — and the form always SENDS a value, so the fallback stops applying
 * to anything built from now on.
 */
const DEFAULT_CASE_SECONDS = 180;

export function TestBuilderPanel() {
  const { toast } = useToast();
  const settings = useTestSettings();
  const cases = useAllCases();
  const createDefinition = useCreateDefinition();
  const patchDefinition = usePatchDefinition();

  const candidates = useQuery({
    queryKey: ["admin", "users", "testrad"],
    queryFn: () => listUsers(),
    select: (rows: UserPublic[]) =>
      rows.filter((u) => u.role === "TESTRAD" && u.active),
  });

  const lastSelection = useQuery({
    queryKey: ["tests", "last-selection"],
    queryFn: fetchLastSelection,
  });

  const [title, setTitle] = useState("");
  // WHICH ROUND this exam runs as, and the only structural difference
  // between the two. Everything else about building one is identical.
  const [kind, setKind] = useState<Round>("ROUND_1");
  // Declared AFTER `kind`, not with the other hooks at the top: it reads
  // that state, and a const is in the temporal dead zone until its own
  // line. Called above, this throws on first render.
  //
  // Scoped to the round being built. An unscoped pool would offer a video
  // round the written application's questions, and the mistake would
  // surface as a candidate meeting a text box on a camera screen.
  const questions = useAllQuestions(kind);
  const [duration, setDuration] = useState("");
  // Blank means this exam offers no practice run at all — which is a
  // different thing from a practice run of zero length.
  const [dryRunDuration, setDryRunDuration] = useState("");
  const [introPrompt, setIntroPrompt] = useState("");
  const [pickedQuestions, setPickedQuestions] = useState<string[]>([]);
  // Cases are picked per PHASE: what a candidate rehearses on is never
  // what they are judged on.
  const [liveCases, setLiveCases] = useState<string[]>([]);
  const [practiceCases, setPracticeCases] = useState<string[]>([]);
  // Minutes per case, VIDEO ROUND only, keyed by case id. Kept as typed
  // text rather than a number so a half-typed value is not coerced to
  // NaN under the user's fingers.
  const [caseMinutes, setCaseMinutes] = useState<Record<string, string>>({});
  // Non-null while an existing exam is being edited rather than a new
  // one built. Editing is possible at all because candidates hold their
  // own sealed copy — see ExamListPanel.
  const [editingId, setEditingId] = useState<string | null>(null);
  // Building a NEW exam. Separate from editingId because "no id yet" and
  // "the dialog is shut" are different things.
  const [creating, setCreating] = useState(false);

  const editingCases = useDefinitionCases(editingId);

  // Pre-tick the previous selection, once, when it arrives.
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (seeded || !lastSelection.data) return;
    setSeeded(true);
    if (lastSelection.data.length) setPickedQuestions(lastSelection.data);
  }, [lastSelection.data, seeded]);

  const defaultDuration = settings.data?.live_default_duration_seconds ?? 0;
  const defaultDryRun = settings.data?.dry_run_duration_seconds ?? 0;

  // Fill both boxes once the defaults arrive, rather than leaving them
  // blank and letting the server pick. The length of an assessment
  // should be a number the person setting it can SEE — a hidden fallback
  // is how a practice run quietly ends up two hours long.
  const prefilledRef = useRef(true);
  useEffect(() => {
    if (!defaultDuration || !prefilledRef.current) return;
    setDuration(String(Math.round(defaultDuration / 60)));
    setDryRunDuration(String(Math.round(defaultDryRun / 60)));
  }, [defaultDuration, defaultDryRun]);

  // When Edit is pressed the exam's own cases arrive asynchronously;
  // seed the pickers once they do, and only once, so a later refetch
  // cannot discard a change made in the meantime.
  const seededCasesFor = useRef<string | null>(null);
  useEffect(() => {
    if (!editingId || !editingCases.data) return;
    if (seededCasesFor.current === editingId) return;
    seededCasesFor.current = editingId;
    setLiveCases(
      editingCases.data.filter((c) => c.phase === "LIVE").map((c) => c.test_case_id),
    );
    setPracticeCases(
      editingCases.data
        .filter((c) => c.phase === "DRY_RUN")
        .map((c) => c.test_case_id),
    );
    setCaseMinutes(
      Object.fromEntries(
        editingCases.data.map((c) => [
          c.test_case_id,
          String(
            Math.round((c.allotted_seconds ?? DEFAULT_CASE_SECONDS) / 60),
          ),
        ]),
      ),
    );
  }, [editingId, editingCases.data]);

  const beginEdit = (exam: {
    id: string;
    title: string;
    kind?: Round;
    duration_seconds: number;
    dry_run_duration_seconds: number | null;
    intro_prompt: string | null;
    selected_question_ids: string[];
  }) => {
    prefilledRef.current = false;
    seededCasesFor.current = null;
    setCreating(false);
    setEditingId(exam.id);
    setCaseMinutes({});
    setTitle(exam.title);
    setDuration(String(Math.round(exam.duration_seconds / 60)));
    setDryRunDuration(
      exam.dry_run_duration_seconds === null
        ? ""
        : String(Math.round(exam.dry_run_duration_seconds / 60)),
    );
    setIntroPrompt(exam.intro_prompt ?? "");
    setKind(exam.kind ?? "ROUND_1");
    setPickedQuestions(exam.selected_question_ids ?? []);
    setLiveCases([]);
    setPracticeCases([]);
  };

  const resetForm = () => {
    setEditingId(null);
    setCreating(false);
    seededCasesFor.current = null;
    setTitle("");
    setIntroPrompt("");
    setLiveCases([]);
    setPracticeCases([]);
    setCaseMinutes({});
    prefilledRef.current = true;
    setDuration(String(Math.round(defaultDuration / 60)));
    setDryRunDuration(String(Math.round(defaultDryRun / 60)));
  };

  const isVideo = kind === "VIDEO_ROUND";

  /** Minutes for one case, falling back to the server's own default so
   *  a newly ticked case is never blank and never silently zero. */
  const minutesForCase = (id: string) => {
    const typed = Number((caseMinutes[id] ?? "").trim());
    return Number.isFinite(typed) && typed > 0
      ? typed
      : DEFAULT_CASE_SECONDS / 60;
  };
  const caseMinutesValue = (id: string) =>
    caseMinutes[id] ?? String(Math.round(DEFAULT_CASE_SECONDS / 60));
  const caseMinutesValid = liveCases.every((id) => {
    const n = Number((caseMinutes[id] ?? "").trim() || DEFAULT_CASE_SECONDS / 60);
    return Number.isFinite(n) && n >= 0.25 && n <= 30;
  });

  // What the candidate actually faces: the sum of the item allowances.
  // NOT the stored outer clock, which the server derives with headroom
  // for disconnections -- showing that number here would tell an admin
  // the round is longer than it is.
  const videoRoundSeconds =
    pickedQuestions.reduce((total, id) => {
      const q = (questions.data ?? []).find((x) => x.id === id);
      const cfg = q?.config_json as { allotted_seconds?: number } | undefined;
      return total + Number(cfg?.allotted_seconds ?? 0);
    }, 0) +
    liveCases.reduce((total, id) => total + minutesForCase(id) * 60, 0);

  const minutesOf = (v: string) => Number(v.trim());
  const validMinutes = (v: string) => {
    const n = minutesOf(v);
    return v.trim().length > 0 && Number.isFinite(n) && n >= 1 && n <= 1440;
  };

  const durationValid = validMinutes(duration);
  // Blank is allowed here: it means "no practice run".
  const dryRunValid = dryRunDuration.trim().length === 0 || validMinutes(dryRunDuration);
  const offersDryRun = dryRunDuration.trim().length > 0 && dryRunValid;

  const canCreate =
    title.trim().length > 0 &&
    // A video round has no total field, so its validity cannot gate the
    // save — the per-case minutes do instead.
    (isVideo ? caseMinutesValid : durationValid) &&
    dryRunValid &&
    !createDefinition.isPending &&
    !patchDefinition.isPending;

  const create = async () => {
    if (!canCreate) return;
    // A video round sends a per-case allowance; a reporting exam sends
    // none, because there the sitting shares one clock and a per-case
    // number would contradict the total above.
    const secondsFor = (id: string) =>
      isVideo
        ? { allotted_seconds: Math.round(minutesForCase(id) * 60) }
        : {};
    const body = {
      title: title.trim(),
      kind,
      duration_seconds: Math.round(minutesOf(duration) * 60),
      dry_run_duration_seconds: offersDryRun
        ? Math.round(minutesOf(dryRunDuration) * 60)
        : null,
      intro_prompt: introPrompt.trim() || null,
      selected_question_ids: pickedQuestions,
      // ONE REQUEST, not create-then-set-cases. The server saves the exam
      // and its cases in one transaction, so a refused case refuses the
      // whole thing: no empty exam per press of Create, and no edit that
      // saved the title but not the cases. The list is sent WHOLE, even
      // empty -- clearing every case is a legitimate edit.
      cases: [
        ...practiceCases.map((id) => ({
          test_case_id: id,
          phase: "DRY_RUN" as const,
        })),
        ...liveCases.map((id) => ({
          test_case_id: id,
          phase: "LIVE" as const,
          ...secondsFor(id),
        })),
      ],
    };
    try {
      if (editingId) {
        await patchDefinition.mutateAsync({ definitionId: editingId, body });
        resetForm();
        toast({ title: "Exam saved" });
        return;
      }
      await createDefinition.mutateAsync(body);
      resetForm();
      toast({ title: "Exam created" });
    } catch (err) {
      toast({
        title: errorDetail(
          err,
          editingId ? "Could not save the exam" : "Could not create the exam",
        ),
        description: "Nothing was saved. Fix the selection and try again.",
        variant: "error",
      });
    }
  };

  // Only a PUBLISHED case can be sat, and the server refuses the rest.
  // Offering them anyway means picking something, pressing Create, and
  // being told no — so they are not offered. The rule is the server's
  // (status READY), not "has images": filtering on instance_count let an
  // uploaded-but-unpublished case through, and that is the one an admin
  // reaches for right after uploading it. What was left out is counted,
  // because a silently shorter list looks like data loss.
  const allCases = cases.data ?? [];
  const sittableCases = allCases.filter(
    (c) => c.status.toUpperCase() === "READY",
  );
  const emptyCaseCount = allCases.filter((c) => c.instance_count === 0).length;
  const unpublishedCaseCount = allCases.filter(
    (c) => c.instance_count > 0 && c.status.toUpperCase() !== "READY",
  ).length;

  const toggle = (list: string[], id: string) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  const examForm = (
    <>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="flex flex-col text-xs text-foreground-muted sm:col-span-2">
            Title
            <input
              data-cy="definition-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="sk-input mt-1 w-auto"
            />
          </label>
          {/* THE ROUND. Everything else on this form is identical for
              both: add cases, set a clock, assign candidates. */}
          <label className="flex flex-col text-xs text-foreground-muted sm:col-span-2">
            Round
            <select
              data-cy="definition-kind"
              value={kind}
              onChange={(e) => {
                setKind(e.target.value as Round);
                // THE SELECTION CANNOT SURVIVE THE ROUND CHANGING.
                // `pickedQuestions` is pre-ticked from this admin's last
                // exam, whatever round that was, and QuestionOrderPicker
                // deliberately TOLERATES an id its pool does not offer --
                // that is how a retired question stays on an exam that
                // already had it. The two together mean ids from the
                // other pool would stay picked, invisibly, and be sealed
                // onto a candidate: typed questions inside a camera
                // round. Clearing is the only honest answer, because a
                // selection made against one pool means nothing in the
                // other.
                setPickedQuestions([]);
              }}
              className="sk-input mt-1 w-auto"
            >
              {(["ROUND_1", "VIDEO_ROUND"] as Round[]).map((r) => (
                <option key={r} value={r}>
                  {ROUND_LABELS[r]}
                </option>
              ))}
            </select>
            <span className="mt-1 text-[11px]">
              {kind === "VIDEO_ROUND"
                ? "Timed questions answered on camera in one take, the last items optionally cases to dictate."
                : "The reporting exam: the written application, an optional practice run, then the cases."}
            </span>
          </label>
          {/* A VIDEO ROUND HAS NO PRACTICE RUN, so it does not get the
              field. Offering it produced exactly the damage this rule
              exists to prevent: a screening round with 5 minutes typed
              here told the candidate, on the one screen they see before
              starting, that a practice run existed -- and there was
              nothing to practise on. The server normalises it away too,
              because a screen that stops offering something is not a
              rule. */}
          {kind !== "VIDEO_ROUND" && (
          <label className="sk-field flex flex-col">
            <span className="sk-label">Practice run (minutes)</span>
            <input
              data-cy="definition-dry-run-duration"
              value={dryRunDuration}
              onChange={(e) => {
                prefilledRef.current = false;
                setDryRunDuration(e.target.value);
              }}
              inputMode="numeric"
              placeholder="none"
              aria-invalid={!dryRunValid}
              className={[
                "mt-1 rounded-md border bg-input-bg px-3 py-2 text-sm text-foreground",
                dryRunValid ? "border-input-border" : "border-status-urgent",
              ].join(" ")}
            />
            <span className="mt-1 text-[11px]">
              {!dryRunValid
                ? "Between 1 and 1440 minutes, or blank."
                : offersDryRun
                  ? "Practice time is never counted against the candidate."
                  : "Leave blank for no practice run."}
            </span>
          </label>
          )}
          {/* A VIDEO ROUND'S TOTAL IS ARITHMETIC, NOT A FIELD. Every
              question and every case carries its own allowance, so
              asking for the total as well invites the two to disagree —
              and they did: 120 minutes was stored on rounds whose items
              added up to 17, with the candidate shown neither. The
              server derives and stores it. A reporting exam keeps this
              field, because there the total IS the model: one clock the
              candidate spends across the practice run and the cases as
              they see fit. */}
          {!isVideo && (
          <label className="sk-field flex flex-col">
            <span className="sk-label">Assessment (minutes)</span>
            <input
              data-cy="definition-duration"
              value={duration}
              onChange={(e) => {
                prefilledRef.current = false;
                setDuration(e.target.value);
              }}
              inputMode="numeric"
              aria-invalid={duration.trim().length > 0 && !durationValid}
              className={[
                "mt-1 rounded-md border bg-input-bg px-3 py-2 text-sm text-foreground",
                duration.trim().length > 0 && !durationValid
                  ? "border-status-urgent"
                  : "border-input-border",
              ].join(" ")}
            />
            <span className="mt-1 text-[11px]">
              {durationValid
                ? `The candidate gets ${formatCountdown(Math.round(minutesOf(duration) * 60))}.`
                : "Between 1 and 1440 minutes."}
            </span>
          </label>
          )}
          {isVideo && (
            <div
              data-cy="video-round-length"
              className="sk-field flex flex-col justify-end"
            >
              <span className="sk-label">Round length</span>
              <p className="mt-1 text-sm text-foreground">
                {formatCountdown(videoRoundSeconds)}
              </p>
              <span className="mt-1 text-[11px] text-foreground-muted">
                Added up from each question and case below. The sitting is
                allowed a little longer than this so a dropped connection
                cannot end it.
              </span>
            </div>
          )}
          <label className="flex flex-col text-xs text-foreground-muted sm:col-span-2">
            {/* Renders UNDER the application's own lede, not instead of
                it -- an admin typing here adds to the standard form
                rather than replacing its explanation. */}
            Note shown on the application, before the cases
            <textarea
              data-cy="definition-intro"
              rows={2}
              value={introPrompt}
              onChange={(e) => setIntroPrompt(e.target.value)}
              className="sk-input mt-1 w-auto"
            />
          </label>
        </div>

        {/* THE PICKER IS BACK, FOR ONE ROUND ONLY -- and that resolves
            the reason it was hidden rather than working around it.

            It was hidden on 2026-08-22 because step one of a REPORTING
            exam is the fixed nine-section candidate application, which
            every candidate answers regardless of what is ticked here. So
            it showed "Introduction questions (0)" on every exam and
            implied candidates were asked nothing, which reads as a
            missing feature and is the opposite of the truth. All of that
            is still true of ROUND_1, so it still does not appear there.

            In a VIDEO_ROUND the questions ARE the exam. There is nothing
            else to sit: the list here, in this order, is what the
            candidate is asked, one at a time, each against its own clock.

            `complete_intro`'s old gate does NOT come back with it. That
            gate refused to let a candidate past step one until every
            mandatory sealed question was answered -- correct while step
            one rendered them, and a permanent dead end once it stopped.
            A video round never goes through that path; it gates itself,
            question by question. See CLAUDE.md. */}
        {kind === "VIDEO_ROUND" && (
          <div className="mt-4">
            <QuestionOrderPicker
              pool={questions.data ?? []}
              picked={pickedQuestions}
              onChange={setPickedQuestions}
              round={kind}
            />
            {(questions.data ?? []).length === 0 && (
              <p
                data-cy="no-video-questions"
                className="mt-2 text-[11px] text-foreground-muted"
              >
                No video round questions yet — add them under Tests →
                Questions → Video round.
              </p>
            )}
          </div>
        )}

        <div className="mt-4 grid grid-cols-1 gap-5">
          <div className="flex flex-col gap-4">
            {emptyCaseCount > 0 && (
              <p
                data-cy="empty-cases-hidden"
                className="sm:col-span-2 text-[11px] text-foreground-muted"
              >
                {emptyCaseCount} case{emptyCaseCount === 1 ? "" : "s"} with no
                images {emptyCaseCount === 1 ? "is" : "are"} not listed — upload
                a study to {emptyCaseCount === 1 ? "it" : "them"} in the Case
                library first.
              </p>
            )}
            {unpublishedCaseCount > 0 && (
              <p
                data-cy="unpublished-cases-hidden"
                className="sm:col-span-2 text-[11px] text-foreground-muted"
              >
                {unpublishedCaseCount} case
                {unpublishedCaseCount === 1 ? " has" : "s have"} images but{" "}
                {unpublishedCaseCount === 1 ? "is" : "are"} not published, so{" "}
                {unpublishedCaseCount === 1 ? "it is" : "they are"} not listed —
                open {unpublishedCaseCount === 1 ? "it" : "them"} in the Case
                library and press Publish.
              </p>
            )}
            {/* Practice first, then assessment — the order the candidate
                meets them in. Asking for the assessed cases first and the
                warm-up second reads backwards against the durations
                above, which are already in sitting order. */}
            {offersDryRun && (
              <CasePicker
                label={`Practice cases (${practiceCases.length})`}
                hint="Separate cases to warm up on. Not assessed."
                cases={sittableCases}
                picked={practiceCases}
                onToggle={(id) => setPracticeCases((v) => toggle(v, id))}
                cy="case-pick-practice"
              />
            )}
            <CasePicker
              label={`Assessment cases (${liveCases.length})`}
              hint={
                isVideo
                  ? "Dictated to camera, one at a time, each with its own clock."
                  : "What the candidate is judged on."
              }
              cases={sittableCases}
              picked={liveCases}
              {...(isVideo
                ? {
                    minutesFor: caseMinutesValue,
                    onMinutes: (id: string, v: string) =>
                      setCaseMinutes((prev) => ({ ...prev, [id]: v })),
                  }
                : {})}
              onToggle={(id) => setLiveCases((v) => toggle(v, id))}
              cy="case-pick-live"
            />
          </div>
        </div>

        <button
          type="button"
          data-cy="btn-create-definition"
          disabled={!canCreate}
          onClick={() => {
            void create();
          }}
          className="sk-btn sk-btn-primary mt-5"
        >
          {editingId ? "Save changes" : "Create exam"}
        </button>
    </>
  );

  // Creating and editing are the SAME form and the same modal. They
  // differ only in whether an id is being written to, so keeping them
  // one dialog is what stops the two drifting apart.
  const formOpen = creating || editingId !== null;

  return (
    <div data-cy="test-builder" className="flex flex-col gap-8">
      <ExamListPanel
        candidates={candidates.data ?? []}
        onEdit={beginEdit}
        onCreate={() => {
          resetForm();
          setCreating(true);
        }}
      />

      {formOpen && (
        <Dialog.Root
          open
          onOpenChange={(next) => {
            if (!next) resetForm();
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
            <Dialog.Content
              data-cy={editingId ? "edit-exam-modal" : "create-exam-modal"}
              className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-full max-w-3xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border border-border bg-background-card shadow-theme-lg"
            >
              <div className="flex items-start justify-between gap-3 border-b border-border px-6 pt-6 pb-4">
                <div>
                  <Dialog.Title className="text-lg font-semibold text-foreground-heading">
                    {editingId ? "Edit exam" : "New exam"}
                  </Dialog.Title>
                  <Dialog.Description className="mt-1 text-xs text-foreground-muted">
                    {editingId
                      ? "Changes apply to candidates assigned from now on. Anyone who already has this exam keeps the version they were given."
                      : "Set the durations and pick the cases. Nothing is offered to anyone until you assign it."}
                  </Dialog.Description>
                </div>
                <Dialog.Close
                  data-cy="btn-close-exam-form"
                  aria-label="Close"
                  className="rounded-md p-1 text-foreground-muted hover:bg-table-row-hover hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  <X className="h-4 w-4" />
                </Dialog.Close>
              </div>
              <div className="px-6 py-5">{examForm}</div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </div>
  );
}

/**
 * Pick the introduction questions AND say what order they come in.
 *
 * The order was always stored -- ``selected_question_ids`` is a list and
 * ``snapshot_questions`` walks it rather than the pool -- but nothing
 * could SET it. A checkbox list appends on tick, so the sequence a
 * candidate met was the sequence an admin happened to click in, which is
 * invisible on screen and impossible to correct without unticking
 * everything and starting again.
 *
 * So the chosen questions are their own list, numbered and movable, and
 * the pool below only offers what is not yet chosen. Two lists rather
 * than one is more chrome, but a single list cannot show both membership
 * and sequence without lying about one of them.
 */
export function QuestionOrderPicker({
  pool,
  picked,
  onChange,
  round = "ROUND_1",
}: {
  pool: { id: string; prompt: string; required?: boolean }[];
  picked: string[];
  onChange: (next: string[]) => void;
  /** Changes what the empty state MEANS, which is the whole reason it
   *  is a prop: with no questions a reporting exam goes straight to the
   *  cases, and a video round has nothing to sit at all. */
  round?: Round;
}) {
  const isVideo = round === "VIDEO_ROUND";
  const byId = new Map(pool.map((q) => [q.id, q]));
  // A retired question can still be selected on an existing exam, so
  // walk the PICKED ids and tolerate one the pool no longer offers
  // rather than silently dropping it from the exam on the next save.
  const chosen = picked.filter((id) => byId.has(id));
  const available = pool.filter((q) => !picked.includes(q.id));

  const move = (from: number, to: number) => {
    if (to < 0 || to >= chosen.length) return;
    const next = [...chosen];
    const [row] = next.splice(from, 1);
    next.splice(to, 0, row!);
    onChange(next);
  };

  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-foreground-muted">
        {isVideo
          ? `Questions (${chosen.length})`
          : `Introduction questions (${chosen.length})`}
      </h4>
      <p className="mt-1 text-[11px] text-foreground-muted">
        {isVideo
          ? "Asked in this order, one at a time. The candidate cannot go back to an earlier one."
          : "Asked in this order, before the cases. Pre-ticked from your last test."}
      </p>

      <ol data-cy="question-order" className="mt-2 flex flex-col gap-1">
        {chosen.map((id, i) => (
          <li
            key={id}
            data-cy="question-chosen"
            className="flex items-start gap-2 rounded-md border border-border bg-background px-2 py-1.5 text-xs text-foreground"
          >
            <span className="w-4 shrink-0 pt-0.5 text-[11px] tabular-nums text-foreground-muted">
              {i + 1}
            </span>
            <span className="min-w-0 flex-1">{byId.get(id)?.prompt}</span>
            <span className="flex shrink-0 items-center gap-0.5">
              <button
                type="button"
                data-cy="btn-question-up"
                aria-label="Move up"
                title="Move up"
                disabled={i === 0}
                onClick={() => move(i, i - 1)}
                className="rounded p-1 text-foreground-muted hover:bg-table-row-hover hover:text-foreground disabled:opacity-30"
              >
                <ChevronUp className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                data-cy="btn-question-down"
                aria-label="Move down"
                title="Move down"
                disabled={i === chosen.length - 1}
                onClick={() => move(i, i + 1)}
                className="rounded p-1 text-foreground-muted hover:bg-table-row-hover hover:text-foreground disabled:opacity-30"
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                data-cy="btn-question-remove"
                aria-label="Remove"
                title="Remove from this exam"
                onClick={() => onChange(chosen.filter((x) => x !== id))}
                className="rounded p-1 text-foreground-muted hover:bg-table-row-hover hover:text-status-urgent"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          </li>
        ))}
        {chosen.length === 0 && (
          <li className="rounded-md border border-dashed border-border px-2 py-2 text-[11px] text-foreground-muted">
            {isVideo
              ? "None chosen — this round has nothing to sit yet."
              : "None chosen — the candidate goes straight to the cases."}
          </li>
        )}
      </ol>

      <p className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-foreground-muted">
        Add from the pool
      </p>
      <ul className="mt-1 flex max-h-36 flex-col gap-0.5 overflow-auto">
        {available.map((q) => (
          <li key={q.id}>
            <button
              type="button"
              data-cy="question-pick"
              onClick={() => onChange([...chosen, q.id])}
              className="flex w-full items-start gap-2 rounded px-2 py-1 text-left text-xs text-foreground-muted hover:bg-table-row-hover hover:text-foreground"
            >
              <Plus className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0">{q.prompt}</span>
            </button>
          </li>
        ))}
        {available.length === 0 && (
          <li className="px-2 text-[11px] text-foreground-muted">
            {pool.length === 0
              ? "No questions in the pool yet — add some under Questions."
              : "Every question in the pool is already on this exam."}
          </li>
        )}
      </ul>
    </div>
  );
}

function CasePicker({
  label,
  hint,
  cases,
  picked,
  onToggle,
  cy,
  minutesFor,
  onMinutes,
}: {
  label: string;
  hint: string;
  cases: { id: string; title: string; modality: string | null }[];
  picked: string[];
  onToggle: (id: string) => void;
  cy: string;
  /** VIDEO ROUND only. Absent means the sitting shares one clock, which
   *  is what a reporting exam does — there the total is the model and a
   *  per-case number would contradict it. */
  minutesFor?: (id: string) => string;
  onMinutes?: (id: string, value: string) => void;
}) {
  const timed = minutesFor !== undefined && onMinutes !== undefined;
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-foreground-muted">
        {label}
      </h4>
      <p className="mt-0.5 text-[11px] text-foreground-muted">{hint}</p>
      <ul className="mt-2 flex max-h-40 flex-col gap-1 overflow-auto">
        {cases.map((c) => {
          const on = picked.includes(c.id);
          return (
          <li key={c.id} className="flex items-start gap-2">
            <label className="flex min-w-0 flex-1 items-start gap-2 text-xs text-foreground">
              <input
                type="checkbox"
                data-cy={cy}
                checked={on}
                onChange={() => onToggle(c.id)}
                className="mt-0.5 h-4 w-4 accent-accent"
              />
              <span className="min-w-0">
                {c.title}
                {c.modality && (
                  <span className="ml-1 text-[11px] text-foreground-muted">
                    ({c.modality})
                  </span>
                )}
              </span>
            </label>
            {/* Only on a case that is actually in the round. A minutes
                box beside an unticked case asks for a number that
                governs nothing. */}
            {timed && on && (
              <span className="flex shrink-0 items-center gap-1">
                <input
                  data-cy={`${cy}-minutes`}
                  aria-label={`Minutes for ${c.title}`}
                  value={minutesFor(c.id)}
                  onChange={(e) => onMinutes(c.id, e.target.value)}
                  inputMode="numeric"
                  className="w-12 rounded-md border border-input-border bg-input-bg px-1.5 py-0.5 text-right text-xs text-foreground"
                />
                <span className="text-[11px] text-foreground-muted">min</span>
              </span>
            )}
          </li>
          );
        })}
        {cases.length === 0 && (
          <li className="text-xs text-foreground-muted">
            No case has images yet — upload a study in the Case library.
          </li>
        )}
      </ul>
    </div>
  );
}
